import React from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import {
  Colors,
  type ColorsType,
  Duration,
  Easing,
  Radius,
  Spacing,
} from '@common/tokens';
import { Text } from '@common/components';

/**
 * Horizontal progress bar with optional text slots: a top-left `title`, a
 * top-right `numbers` readout (defaults to the computed percentage) and a
 * bottom-left `description`.
 *
 * The fill is a neutral gray by default and turns `grass` green when the bar
 * is full (100%); an explicit `color` token overrides both.
 */
export function ProgressBar({
  value,
  max = 100,
  color,
  title,
  numbers,
  description,
  className,
}: {
  /** Current amount. */
  value: number;
  /** Total amount the bar represents. Defaults to 100. */
  max?: number;
  /**
   * Fill color token. Defaults to the neutral gray `steelDarkest`, switching
   * to `grass` when the bar is full (100%). Pass a token to override both.
   */
  color?: ColorsType;
  /** Top-left slot (e.g. "FTE"). */
  title?: ReactNode;
  /**
   * Top-right slot (e.g. "84h of 108h"). When `undefined`, falls back to the
   * computed percentage; pass `null` to hide it.
   */
  numbers?: ReactNode;
  /** Bottom-left slot (e.g. "24h remaining"), rendered as secondary text. */
  description?: ReactNode;
  className?: string;
}) {
  const percent = computePercent(value, max);
  const clampedValue = Math.min(max, Math.max(0, value));
  const fillColor = color ?? (percent === 100 ? 'grass' : 'steelDarkest');
  const numbersSlot = numbers === undefined ? `${percent}%` : numbers;
  const hasTitle = isPresent(title);
  const hasNumbers = isPresent(numbersSlot);
  const hasDescription = isPresent(description);
  const hasHeader = hasTitle || hasNumbers;

  return (
    <div className={clsx('wk-progress-bar', className)}>
      {hasHeader && (
        <div className="wk-progress-bar__header">
          <span className="wk-progress-bar__title">
            {hasTitle && (
              <Text size="md" weight="medium">
                {title}
              </Text>
            )}
          </span>
          {hasNumbers && (
            <span className="wk-progress-bar__numbers">
              <Text size="md" weight="medium">
                {numbersSlot}
              </Text>
            </span>
          )}
        </div>
      )}
      <div
        className="wk-progress-bar__track"
        role="progressbar"
        aria-valuenow={clampedValue}
        aria-valuemin={0}
        aria-valuemax={max}
      >
        <div
          className="wk-progress-bar__fill"
          style={{
            width: `${percent}%`,
            backgroundColor: Colors[fillColor],
          }}
        />
      </div>
      {hasDescription && (
        <Text size="sm" secondary>
          {description}
        </Text>
      )}
      <style jsx>{`
        .wk-progress-bar {
          display: flex;
          flex-direction: column;
          gap: ${Spacing.sm}px;
          width: 100%;
        }

        .wk-progress-bar__header {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
        }

        .wk-progress-bar__track {
          height: 6px;
          background-color: ${Colors.steelLighter};
          border-radius: ${Radius.sm};
          overflow: hidden;
        }

        .wk-progress-bar__fill {
          height: 100%;
          border-radius: ${Radius.sm};
          transition:
            width ${Duration.normal} ${Easing.ease},
            background-color ${Duration.normal} ${Easing.ease};
        }
      `}</style>
    </div>
  );
}

function isPresent(slot: ReactNode): boolean {
  return slot !== undefined && slot !== null;
}

function computePercent(value: number, max: number): number {
  if (max <= 0) return 0;

  return Math.round(Math.min(100, Math.max(0, (value / max) * 100)));
}
